import React, { useState, useRef, useCallback, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { ChatAttachment } from '@/lib/types'
import { Send, Paperclip, X, File, Upload, FileText, FileImage, FileVideo, Music, Archive, Code, CheckCircle2, Trash2, Database, Search, Image as ImageIcon, Square, Globe } from 'lucide-react'
import { storage, storageKeys } from '@/lib/storage'
import { ModelSelector } from '@/components/ModelSelector'

interface ChatInputProps {
  onSendMessage: (content: string, attachments?: ChatAttachment[]) => void
  onFileUpload: (files: File[]) => Promise<ChatAttachment[]>
  disabled?: boolean
  placeholder?: string
  sharepointSearchEnabled?: boolean
  onSharepointSearchToggle?: (enabled: boolean) => void
  imageGeneratorEnabled?: boolean
  onImageGeneratorToggle?: (enabled: boolean) => void
  webParserEnabled?: boolean
  onWebParserToggle?: (enabled: boolean) => void
  isStreaming?: boolean
  onStopGeneration?: () => void
  conversationId?: number | null
  currentModelId?: string | null
  onModelChange?: (modelId: string) => void
}

export function ChatInput({ 
  onSendMessage, 
  onFileUpload, 
  disabled = false, 
  placeholder = "Type your message...",
  sharepointSearchEnabled = false,
  onSharepointSearchToggle,
  imageGeneratorEnabled = false,
  onImageGeneratorToggle,
  webParserEnabled = false,
  onWebParserToggle,
  isStreaming = false,
  onStopGeneration,
  conversationId = null,
  currentModelId = null,
  onModelChange
}: ChatInputProps) {
  const [content, setContent] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [attachments, setAttachments] = useState<ChatAttachment[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const [dragCounter, setDragCounter] = useState(0)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({})
  const [uploadSuccess, setUploadSuccess] = useState<Record<string, boolean>>({})
  const [showUploadSuccess, setShowUploadSuccess] = useState(false)
  const [inputHeight, setInputHeight] = useState(() => {
    const saved = storage.getItem(storageKeys.INPUT_HEIGHT)
    return saved ? Number(saved) : 52
  })
  const [isResizing, setIsResizing] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const resizeRef = useRef<HTMLDivElement>(null)

  // Validation constants
  const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB
  const MAX_FILES = 10
  const ALLOWED_TYPES = [
    // Images
    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
    // Documents
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    // Text files
    'text/plain', 'text/csv', 'text/markdown',
    // Archives
    'application/zip', 'application/x-zip-compressed',
    // Code files (common types)
    'application/json', 'text/html', 'text/css', 'application/javascript'
  ]

  // File type icon utility
  const getFileIcon = (filename: string, mimeType?: string) => {
    const extension = filename.toLowerCase().split('.').pop()
    
    // Check by MIME type first
    if (mimeType?.startsWith('image/')) return FileImage
    if (mimeType?.startsWith('video/')) return FileVideo
    if (mimeType?.startsWith('audio/')) return Music
    
    // Check by extension
    if (['pdf'].includes(extension!)) return FileText
    if (['doc', 'docx', 'txt', 'rtf', 'md'].includes(extension!)) return FileText
    if (['xls', 'xlsx', 'csv'].includes(extension!)) return FileText
    if (['ppt', 'pptx'].includes(extension!)) return FileText
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension!)) return Archive
    if (['js', 'ts', 'jsx', 'tsx', 'py', 'java', 'cpp', 'c', 'html', 'css', 'json', 'xml'].includes(extension!)) return Code
    
    return File
  }

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  // File validation
  const validateFiles = useCallback((files: File[]): { validFiles: File[]; errors: string[] } => {
    const errors: string[] = []
    const validFiles: File[] = []

    // Check total file count
    if (attachments.length + files.length > MAX_FILES) {
      errors.push(`Cannot upload more than ${MAX_FILES} files total. You currently have ${attachments.length} files.`)
      return { validFiles, errors }
    }

    for (const file of files) {
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`"${file.name}" is too large. Maximum file size is ${formatFileSize(MAX_FILE_SIZE)}.`)
        continue
      }

      // Check file type
      const isAllowedType = ALLOWED_TYPES.includes(file.type) || 
                           file.name.toLowerCase().endsWith('.txt') ||
                           file.name.toLowerCase().endsWith('.md') ||
                           file.name.toLowerCase().endsWith('.json') ||
                           file.name.toLowerCase().endsWith('.js') ||
                           file.name.toLowerCase().endsWith('.ts') ||
                           file.name.toLowerCase().endsWith('.jsx') ||
                           file.name.toLowerCase().endsWith('.tsx')

      if (!isAllowedType) {
        errors.push(`"${file.name}" is not a supported file type.`)
        continue
      }

      validFiles.push(file)
    }

    return { validFiles, errors }
  }, [attachments.length])


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if ((!content.trim() && attachments.length === 0) || disabled) return

    try {
      onSendMessage(content.trim(), attachments.length > 0 ? attachments : undefined)
      setContent('')
      setAttachments([])
      setValidationErrors([])
      setUploadProgress({})
      setUploadSuccess({})
      setShowUploadSuccess(false)
      
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }



  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    
    if (files.length === 0) return

    // Validate files first
    const { validFiles, errors } = validateFiles(files)
    
    if (errors.length > 0) {
      setValidationErrors(errors)
    }

    if (validFiles.length === 0) {
      setIsUploading(false)
      e.target.value = ''
      return
    }

    try {
      setIsUploading(true)
      
      // Initialize progress for each file
      const progressMap: Record<string, number> = {}
      validFiles.forEach((file, index) => {
        progressMap[`${file.name}-${index}`] = 0
      })
      setUploadProgress(progressMap)

      const uploadedFiles = await Promise.all(
        validFiles.map(async (file, index) => {
          const fileKey = `${file.name}-${index}`
          
          // Simulate progress updates
          const progressInterval = setInterval(() => {
            setUploadProgress(prev => ({
              ...prev,
              [fileKey]: Math.min(prev[fileKey] + 10 + Math.random() * 20, 90)
            }))
          }, 150)

          try {
            const result = await onFileUpload([file])
            if (!result || !result[0]) {
              throw new Error('Upload returned empty result')
            }
            clearInterval(progressInterval)
            setUploadProgress(prev => ({
              ...prev,
              [fileKey]: 100
            }))
            setUploadSuccess(prev => ({
              ...prev,
              [fileKey]: true
            }))
            
            return result[0]
          } catch (error) {
            clearInterval(progressInterval)
            setUploadProgress(prev => {
              const newProgress = { ...prev }
              delete newProgress[fileKey]
              return newProgress
            })
            setUploadSuccess(prev => {
              const newSuccess = { ...prev }
              delete newSuccess[fileKey]
              return newSuccess
            })
            throw error
          }
        })
      )
      
      setAttachments(prev => [...prev, ...uploadedFiles])
      
      // Show upload success message
      setShowUploadSuccess(true)
      
      // Clear progress and success indicators after showing completion
      setTimeout(() => {
        setUploadProgress({})
        setUploadSuccess({})
        setShowUploadSuccess(false)
      }, 3000)
      
    } catch (error) {
      console.error('File upload failed:', error)
      setUploadProgress({})
      setUploadSuccess({})
      setShowUploadSuccess(false)
      setValidationErrors(prev => [...prev, 'Upload failed. Please try again.'])
    } finally {
      setIsUploading(false)
    }
    
    // Reset file input
    e.target.value = ''
  }

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragCounter(prev => prev + 1)
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true)
    }
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragCounter(prev => {
      const newCount = prev - 1
      if (newCount <= 0) {
        setIsDragOver(false)
        return 0
      }
      return newCount
    })
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    setDragCounter(0)

    if (disabled || isUploading) return

    const files = Array.from(e.dataTransfer.files)
    
    // Inline upload logic to avoid circular dependencies
    if (files.length === 0) return

    // Validate files first
    const { validFiles, errors } = validateFiles(files)
    
    if (errors.length > 0) {
      setValidationErrors(errors)
    }

    if (validFiles.length === 0) {
      setIsUploading(false)
      return
    }

    try {
      setIsUploading(true)
      
      // Initialize progress for each file
      const progressMap: Record<string, number> = {}
      validFiles.forEach((file, index) => {
        progressMap[`${file.name}-${index}`] = 0
      })
      setUploadProgress(progressMap)

      const uploadedFiles = await Promise.all(
        validFiles.map(async (file, index) => {
          const fileKey = `${file.name}-${index}`
          
          // Simulate progress updates
          const progressInterval = setInterval(() => {
            setUploadProgress(prev => ({
              ...prev,
              [fileKey]: Math.min(prev[fileKey] + 10 + Math.random() * 20, 90)
            }))
          }, 150)

          try {
            const result = await onFileUpload([file])
            if (!result || !result[0]) {
              throw new Error('Upload returned empty result')
            }
            clearInterval(progressInterval)
            setUploadProgress(prev => ({
              ...prev,
              [fileKey]: 100
            }))
            setUploadSuccess(prev => ({
              ...prev,
              [fileKey]: true
            }))
            
            return result[0]
          } catch (error) {
            clearInterval(progressInterval)
            setUploadProgress(prev => {
              const newProgress = { ...prev }
              delete newProgress[fileKey]
              return newProgress
            })
            setUploadSuccess(prev => {
              const newSuccess = { ...prev }
              delete newSuccess[fileKey]
              return newSuccess
            })
            throw error
          }
        })
      )
      
      setAttachments(prev => [...prev, ...uploadedFiles])
      
      // Show upload success message
      setShowUploadSuccess(true)
      
      // Clear progress and success indicators after showing completion
      setTimeout(() => {
        setUploadProgress({})
        setUploadSuccess({})
        setShowUploadSuccess(false)
      }, 3000)
      
    } catch (error) {
      console.error('File upload failed:', error)
      setUploadProgress({})
      setUploadSuccess({})
      setShowUploadSuccess(false)
      setValidationErrors(prev => [...prev, 'Upload failed. Please try again.'])
    } finally {
      setIsUploading(false)
    }
  }, [disabled, isUploading, validateFiles, onFileUpload])

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index))
  }

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      const scrollHeight = textareaRef.current.scrollHeight
      const newHeight = Math.max(inputHeight, Math.min(scrollHeight, 300))
      textareaRef.current.style.height = `${newHeight}px`
    }
  }

  // Handle resize drag
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
  }, [])

  // Handle resize drag
  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e: MouseEvent) => {
      if (!textareaRef.current) return
      
      const rect = textareaRef.current.getBoundingClientRect()
      const newHeight = rect.bottom - e.clientY
      const minHeight = 52
      const maxHeight = 300
      const clampedHeight = Math.max(minHeight, Math.min(maxHeight, newHeight))
      
      setInputHeight(clampedHeight)
      if (textareaRef.current) {
        textareaRef.current.style.height = `${clampedHeight}px`
      }
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      if (textareaRef.current) {
        const currentHeight = textareaRef.current.offsetHeight
        storage.setItem(storageKeys.INPUT_HEIGHT, currentHeight)
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing])

  // Apply saved height on mount
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = `${inputHeight}px`
    }
  }, [inputHeight])

  // Prefill from template draft if available
  useEffect(() => {
    const draft = storage.getItem<{ content: string }>(storageKeys.DRAFT_MESSAGE, null)
    if (draft && draft.content) {
      setContent(draft.content.substring(0, 20000))
      storage.removeItem(storageKeys.DRAFT_MESSAGE)
      // Adjust height after setting content
      setTimeout(adjustTextareaHeight, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Handle paste events for image uploads
  const handlePaste = useCallback(async (e: ClipboardEvent) => {
    if (disabled || isUploading) return

    const items = e.clipboardData?.items
    if (!items) return

    const files: File[] = []
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      
      // Check if it's a file (image from clipboard)
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile()
        if (file) {
          files.push(file)
        }
      }
    }

    if (files.length > 0) {
      e.preventDefault() // Prevent default paste behavior
      
      // Validate files first
      const { validFiles, errors } = validateFiles(files)
      
      if (errors.length > 0) {
        setValidationErrors(errors)
      }

      if (validFiles.length === 0) return

      try {
        setIsUploading(true)
        
        // Initialize progress for each file
        const progressMap: Record<string, number> = {}
        validFiles.forEach((file, index) => {
          progressMap[`${file.name}-${index}`] = 0
        })
        setUploadProgress(progressMap)

        const uploadedFiles = await Promise.all(
          validFiles.map(async (file, index) => {
            const fileKey = `${file.name}-${index}`
            
            // Simulate progress updates
            const progressInterval = setInterval(() => {
              setUploadProgress(prev => ({
                ...prev,
                [fileKey]: Math.min(prev[fileKey] + 10 + Math.random() * 20, 90)
              }))
            }, 150)

            try {
              const result = await onFileUpload([file])
              if (!result || !result[0]) {
                throw new Error('Upload returned empty result')
              }
              clearInterval(progressInterval)
              setUploadProgress(prev => ({
                ...prev,
                [fileKey]: 100
              }))
              setUploadSuccess(prev => ({
                ...prev,
                [fileKey]: true
              }))
              
              return result[0]
            } catch (error) {
              clearInterval(progressInterval)
              setUploadProgress(prev => {
                const newProgress = { ...prev }
                delete newProgress[fileKey]
                return newProgress
              })
              setUploadSuccess(prev => {
                const newSuccess = { ...prev }
                delete newSuccess[fileKey]
                return newSuccess
              })
              throw error
            }
          })
        )
        
        setAttachments(prev => [...prev, ...uploadedFiles])
        
        // Show upload success message
        setShowUploadSuccess(true)
        
        // Clear progress and success indicators after showing completion
        setTimeout(() => {
          setUploadProgress({})
          setUploadSuccess({})
          setShowUploadSuccess(false)
        }, 3000)
        
      } catch (error) {
        console.error('Paste upload failed:', error)
        setUploadProgress({})
        setUploadSuccess({})
        setShowUploadSuccess(false)
        setValidationErrors(prev => [...prev, 'Paste upload failed. Please try again.'])
      } finally {
        setIsUploading(false)
      }
    }
  }, [disabled, isUploading, validateFiles, onFileUpload])

  // Add paste event listener
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.addEventListener('paste', handlePaste)
      return () => {
        textarea.removeEventListener('paste', handlePaste)
      }
    }
  }, [handlePaste])

  return (
    <div 
      className="relative p-4 bg-gradient-to-t from-gray-50 to-white dark:from-gray-900 dark:to-gray-800"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      role="region"
      aria-label="Chat input area with file upload"
    >
      {/* Drag overlay */}
      {isDragOver && (
        <div className="absolute inset-0 z-50 bg-emerald-500/20 border-4 border-dashed border-emerald-500 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg border border-emerald-500">
            <Upload className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
            <p className="text-lg font-medium text-emerald-700 dark:text-emerald-400 text-center">
              Drop files here to upload
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center mt-1">
              Release to upload your files
            </p>
          </div>
        </div>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="max-w-4xl mx-auto mb-3">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <div className="flex items-start">
              <X className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="ml-2 flex-1">
                <h4 className="text-sm font-medium text-red-800 dark:text-red-200">
                  {validationErrors.length === 1 ? 'Upload Error' : 'Upload Errors'}
                </h4>
                <ul className="mt-1 text-sm text-red-700 dark:text-red-300">
                  {validationErrors.map((error, index) => (
                    <li key={index} className="mt-1">• {error}</li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => setValidationErrors([])}
                className="ml-2 p-1 hover:bg-red-100 dark:hover:bg-red-800 rounded"
                title="Dismiss errors"
              >
                <X className="w-4 h-4 text-red-500" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Success Notification */}
      {showUploadSuccess && (
        <div className="max-w-4xl mx-auto mb-3">
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <span className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                {attachments.filter(a => a).length === 1 ? 'File uploaded successfully!' : `${attachments.filter(a => a).length} files uploaded successfully!`}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Uploaded Files Display */}
      {attachments.length > 0 && (
        <div className="max-w-4xl mx-auto mb-3">
          <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-3">
              <File className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Uploaded Files ({attachments.length})
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {attachments.map((attachment, index) => {
                if (!attachment || !attachment.filename || !attachment.mime) {
                  console.error('Invalid attachment object:', attachment)
                  return null
                }
                
                const IconComponent = getFileIcon(attachment.filename, attachment.mime)
                
                return (
                  <div
                    key={attachment.id}
                    className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-emerald-200 dark:border-emerald-700 rounded-lg shadow-sm hover:shadow-md transition-all hover:border-emerald-300 dark:hover:border-emerald-600"
                  >
                    <IconComponent className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300 block truncate max-w-[150px]">
                        {attachment.filename}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {formatFileSize(attachment.file_size)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors group"
                      title="Remove file"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-gray-400 group-hover:text-red-500 transition-colors" />
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="flex gap-3 max-w-4xl mx-auto relative z-10">
        <div className="flex-1 relative">
          {/* Resize handle */}
          <div
            ref={resizeRef}
            onMouseDown={handleResizeStart}
            className={`absolute top-0 left-0 right-0 h-3 cursor-ns-resize z-20 hover:bg-emerald-500/30 transition-colors rounded-t-xl ${
              isResizing ? 'bg-emerald-500/40' : 'bg-transparent'
            }`}
            style={{ marginTop: '-6px' }}
            title="Drag to resize input height"
          />
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => {
              setContent(e.target.value)
              adjustTextareaHeight()
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className="w-full px-4 py-3 pr-12 border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-50 shadow-sm transition-all"
            style={{ height: `${inputHeight}px`, minHeight: '52px', maxHeight: '300px' }}
            rows={1}
          />
          
          <input
            ref={fileInputRef}
            type="file"
            id="file-upload"
            className="hidden"
            multiple
            onChange={handleFileUpload}
            disabled={disabled || isUploading}
          />
          
          <label
            htmlFor="file-upload"
            className={`absolute right-3 top-3 p-2 rounded-lg transition-all ${
              disabled || isUploading 
                ? 'opacity-50 cursor-not-allowed' 
                : 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 focus-within:ring-2 focus-within:ring-emerald-500'
            }`}
            aria-label="Attach files"
            title="Attach files (or drag and drop)"
          >
            <Paperclip className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </label>
        </div>

        {isStreaming && onStopGeneration ? (
          <Button
            type="button"
            onClick={onStopGeneration}
            size="icon"
            className="h-[52px] w-[52px] bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-md transition-all"
            title="Stop generation"
          >
            <Square className="w-5 h-5" />
          </Button>
        ) : (
          <Button
            type="submit"
            disabled={(!content.trim() && attachments.length === 0) || disabled || isUploading}
            size="icon"
            className="h-[52px] w-[52px] bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white shadow-md transition-all disabled:opacity-50"
          >
            <Send className="w-5 h-5" />
          </Button>
        )}
      </form>

      {/* Toggle buttons row below input */}
      <div className="flex gap-2 max-w-4xl mx-auto mt-2 justify-start items-center">
        {/* Model Selector - placed before tool buttons */}
        <ModelSelector
          conversationId={conversationId}
          currentModelId={currentModelId}
          onModelChange={onModelChange}
          disabled={disabled || isStreaming}
        />
        
        {/* Tool Toggle Buttons */}
          {/* Image Generator Toggle */}
          {onImageGeneratorToggle && (
            <button
              type="button"
              onClick={() => onImageGeneratorToggle(!imageGeneratorEnabled)}
              className={`h-8 px-3 flex items-center gap-2 rounded-lg transition-all text-xs font-medium border ${
                disabled || isUploading
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700'
              } ${
                imageGeneratorEnabled 
                  ? 'text-blue-700 bg-blue-50 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800' 
                  : 'text-gray-500 border-transparent dark:text-gray-400'
              }`}
              title={`Generate image for this message. Currently: ${imageGeneratorEnabled ? 'On' : 'Off'}`}
              aria-label="Toggle Image Generator"
              aria-pressed={imageGeneratorEnabled}
            >
              <ImageIcon className={`w-4 h-4 ${imageGeneratorEnabled ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
              <span>Image</span>
            </button>
          )}

          {/* Giatec Knowledge (SharePoint Search) Toggle */}
          {onSharepointSearchToggle && (
            <button
              type="button"
              onClick={() => onSharepointSearchToggle(!sharepointSearchEnabled)}
              className={`h-8 px-3 flex items-center gap-2 rounded-lg transition-all text-xs font-medium border ${
                disabled || isUploading
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700'
              } ${
                sharepointSearchEnabled 
                  ? 'text-emerald-700 bg-emerald-50 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800' 
                  : 'text-gray-500 border-transparent dark:text-gray-400'
              }`}
              title={`Use internal documents as knowledge. Currently: ${sharepointSearchEnabled ? 'On' : 'Off'}`}
              aria-label="Toggle Giatec Knowledge"
              aria-pressed={sharepointSearchEnabled}
            >
              <Database className={`w-4 h-4 ${sharepointSearchEnabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`} />
              <span>Giatec Knowledge</span>
            </button>
          )}
          
          {/* Web Parser Toggle */}
          {onWebParserToggle && (
            <button
              type="button"
              onClick={() => onWebParserToggle(!webParserEnabled)}
              className={`h-8 px-3 flex items-center gap-2 rounded-lg transition-all text-xs font-medium border ${
                disabled || isUploading
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700'
              } ${
                webParserEnabled 
                  ? 'text-purple-700 bg-purple-50 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800' 
                  : 'text-gray-500 border-transparent dark:text-gray-400'
              }`}
              title={`Search websites to find answers. Currently: ${webParserEnabled ? 'On' : 'Off'}`}
              aria-label="Toggle Web Parser"
              aria-pressed={webParserEnabled}
            >
              <Globe className={`w-4 h-4 ${webParserEnabled ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400'}`} />
              <span>Web Parser</span>
            </button>
          )}
        </div>

      {/* Upload Progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className="max-w-4xl mx-auto mt-3">
          {Object.entries(uploadProgress).map(([fileKey, progress]) => {
            const fileName = fileKey.split('-').slice(0, -1).join('-') || fileKey
            const progressValue = typeof progress === 'number' ? progress : Number(progress) || 0
            const isComplete = progressValue === 100
            const hasSuccessIndicator = uploadSuccess[fileKey]
            
            return (
              <div key={fileKey} className={`mb-2 p-3 border rounded-lg transition-all duration-500 ${
                hasSuccessIndicator 
                  ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800' 
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {hasSuccessIndicator ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    ) : (
                      <Upload className="w-4 h-4 text-blue-500 flex-shrink-0 animate-pulse" />
                    )}
                    <span className={`text-sm truncate ${
                      hasSuccessIndicator 
                        ? 'text-emerald-700 dark:text-emerald-300 font-medium' 
                        : 'text-gray-700 dark:text-gray-300'
                    }`}>
                      {hasSuccessIndicator ? `${fileName} uploaded!` : `Uploading ${fileName}...`}
                    </span>
                  </div>
                  <span className={`text-xs ${
                    hasSuccessIndicator ? 'text-emerald-600' : 'text-muted-foreground'
                  }`}>
                    {Math.round(progressValue)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ease-out ${
                      hasSuccessIndicator ? 'bg-emerald-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${progressValue}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}

      {isUploading && Object.keys(uploadProgress).length === 0 && (
        <p className="text-xs text-muted-foreground mt-2 text-center">
          Preparing files for upload...
        </p>
      )}
      
      <p className="text-xs text-center text-muted-foreground mt-2">
        Press <kbd className="px-1.5 py-0.5 text-xs bg-gray-200 dark:bg-gray-700 rounded">Enter</kbd> to send, <kbd className="px-1.5 py-0.5 text-xs bg-gray-200 dark:bg-gray-700 rounded">Shift+Enter</kbd> for new line.
        <span className="block mt-1">
          Drag and drop files, click the <Paperclip className="inline w-3 h-3" /> icon, or <kbd className="px-1.5 py-0.5 text-xs bg-gray-200 dark:bg-gray-700 rounded">Ctrl+V</kbd> to paste images. Max {formatFileSize(MAX_FILE_SIZE)} per file, {MAX_FILES} files total.
        </span>
      </p>
    </div>
  )
}

